import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Leaf,
  Music,
  Phone,
  Sparkles,
  Sun,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, KineticLines, CountNumber } from "@/components/anim";
import ParallaxImage from "@/components/ParallaxImage";
import Marquee from "@/components/Marquee";
import GlowCard from "@/components/GlowCard";
import Embers from "@/components/Embers";
import HeroVideo from "@/components/HeroVideo";
import { PHONE_TEL, PHONE_DISPLAY } from "@/site";

const CHIP_ICONS = [Sun, Leaf, Music, Sparkles];

export default function Home() {
  const { t } = useLang();
  const h = t.hero;
  const name = h.name;
  // after the intro loader the hero reveals immediately on route changes
  const introBase = window.__loaderDone ? 0.15 : 1.75;

  return (
    <div>
      {/* HERO */}
      <section
        className="relative flex h-[100svh] min-h-[600px] items-end overflow-hidden"
        data-testid="home-hero"
      >
        <HeroVideo
          poster="/img/forno.webp"
          verticalPoster="/video/hero_verticale_poster.webp"
          strength={14}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/25 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-ink/10 to-transparent" />
        <Embers />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 sm:px-10 sm:pb-28">
          <motion.p
            className="eyebrow mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: introBase + 0.15, duration: 1 }}
          >
            {h.eyebrow} — {h.place}
          </motion.p>

          <h1 className="font-display italic leading-[0.82] text-white text-[clamp(4.5rem,18vw,15rem)]">
            <KineticLines
              mode="load"
              delay={introBase}
              lines={[
                <>
                  {name.slice(0, -1)}
                  <span className="text-gold">{name.slice(-1)}</span>
                </>,
              ]}
            />
          </h1>

          <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div>
              <KineticLines
                mode="load"
                delay={introBase + 0.35}
                lines={[
                  <span className="font-display italic text-2xl text-gold-light sm:text-3xl">
                    {h.tagline}
                  </span>,
                ]}
              />
              <motion.p
                className="mt-4 max-w-xl text-sm leading-relaxed text-neutral-300 sm:text-base"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: introBase + 0.55, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {h.sub}
              </motion.p>
              <motion.div
                className="mt-8 flex flex-wrap items-center gap-4"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: introBase + 0.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              >
                <a
                  href={PHONE_TEL}
                  data-testid="hero-tel-cta"
                  className="inline-flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-black transition-colors duration-500 hover:bg-gold-light"
                >
                  <Phone size={15} />
                  {h.ctaBook}
                </a>
                <Link
                  to="/menu"
                  data-testid="hero-menu-cta"
                  className="group inline-flex items-center gap-3 border border-white/30 px-8 py-4 text-[13px] uppercase tracking-[0.18em] text-white transition-all duration-500 hover:border-gold hover:text-gold"
                >
                  {h.ctaMenu}
                  <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>

        <motion.div
          className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: introBase + 1.35, duration: 1 }}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
            {h.scroll}
          </span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          >
            <ChevronDown size={16} className="text-gold" />
          </motion.span>
        </motion.div>
      </section>

      <Marquee />

      {/* HIGHLIGHTS */}
      <section className="py-24 sm:py-32" data-testid="home-highlights">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between sm:mb-20">
            <div>
              <Reveal>
                <p className="eyebrow">{t.highlights.eyebrow}</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 whitespace-pre-line font-display text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                  {t.highlights.title}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <p className="max-w-sm text-sm leading-relaxed text-neutral-400">
                {t.highlights.intro}
              </p>
            </Reveal>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {t.highlights.cards.map((card, i) => (
              <Reveal key={card.n} delay={(i % 3) * 0.12} className="h-full">
                <GlowCard
                  as="article"
                  data-testid={`highlight-card-${i}`}
                  className="group relative h-full overflow-hidden border border-gold/10 bg-coal"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={card.img}
                      alt={card.title}
                      loading="lazy"
                      className="zoomable h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
                    <span className="absolute right-6 top-5 font-display text-3xl italic text-gold/70">
                      {card.n}
                    </span>
                  </div>
                  <div className="relative -mt-24 p-7 pt-0">
                    <h3 className="font-display text-2xl text-white sm:text-[1.7rem]">
                      {card.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-300">
                      {card.text}
                    </p>
                  </div>
                </GlowCard>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-wrap items-center gap-3">
              {t.highlights.chips.map((chip, i) => {
                const Icon = CHIP_ICONS[i % CHIP_ICONS.length];
                return (
                  <span
                    key={chip}
                    data-testid={`highlight-chip-${i}`}
                    className="inline-flex items-center gap-2.5 border border-gold/20 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-light transition-colors duration-300 hover:border-gold/60"
                  >
                    <Icon size={13} />
                    {chip}
                  </span>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <section
        className="relative overflow-hidden bg-coal pb-32 pt-24 sm:pb-44 sm:pt-32"
        data-testid="home-manifesto"
      >
        {/* fire texture on the right edge */}
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 lg:block" aria-hidden="true">
          <ParallaxImage
            src="/img/fiamme.webp"
            alt=""
            strength={10}
            className="h-full w-full opacity-[0.16]"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-coal/20 via-coal/70 to-coal" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-10">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div>
                <Reveal>
                  <p className="eyebrow">{t.manifesto.eyebrow}</p>
                </Reveal>
                <Reveal delay={0.1}>
                  <h2 className="mt-5 whitespace-pre-line font-display italic text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                    {t.manifesto.title}
                  </h2>
                </Reveal>
              </div>
            </div>
            <div className="lg:col-span-8">
              {t.manifesto.items.map((item, i) => (
                <Reveal key={item.n} delay={i * 0.06} y={44}>
                  <div
                    data-testid={`manifesto-item-${i}`}
                    className="group flex flex-col gap-5 border-t border-gold/15 py-11 sm:flex-row sm:gap-12 sm:py-14"
                  >
                    <span className="font-display text-5xl italic text-gold/35 transition-colors duration-500 group-hover:text-gold sm:text-6xl">
                      <CountNumber value={parseInt(item.n, 10)} />
                    </span>
                    <div>
                      <h3 className="font-display text-2xl text-white sm:text-3xl">
                        {item.title}
                      </h3>
                      <p className="mt-4 max-w-xl text-sm leading-relaxed text-neutral-400 sm:text-base">
                        {item.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-gold/15" />
            </div>
          </div>
        </div>
      </section>

      {/* PARALLAX QUOTE */}
      <section
        className="relative h-[65vh] overflow-hidden sm:h-[75vh]"
        data-testid="home-quote-band"
      >
        <ParallaxImage
          src="/img/esterno.webp"
          alt="Ristorante Gunè di sera"
          strength={16}
          className="absolute inset-0 h-full"
        />
        <div className="absolute inset-0 bg-ink/55" />
        <Embers className="opacity-70" />
        <div className="relative z-10 flex h-full items-center justify-center px-5">
          <div className="max-w-3xl text-center">
            <Reveal>
              <p className="font-display text-2xl italic leading-snug text-white sm:text-4xl lg:text-[2.9rem]">
                “{t.quote.text}”
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                {t.quote.author}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-24 sm:py-32" data-testid="home-cta-band">
        <Embers className="opacity-60" />
        <div className="relative z-10 mx-auto max-w-4xl px-5 text-center sm:px-10">
          <Reveal>
            <p className="eyebrow">{t.ctaBand.eyebrow}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display italic text-4xl text-white sm:text-6xl">
              {t.ctaBand.title}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-neutral-400 sm:text-base">
              {t.ctaBand.text}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              href={PHONE_TEL}
              data-testid="cta-band-tel"
              className="link-gold mt-10 inline-block font-display text-3xl italic text-gold sm:text-5xl"
            >
              {PHONE_DISPLAY}
            </a>
          </Reveal>
          <Reveal delay={0.4}>
            <Link
              to="/contatti#prenota"
              data-testid="cta-band-form-link"
              className="mt-9 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-400 transition-colors duration-300 hover:text-gold"
            >
              {t.ctaBand.orForm}
              <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
